import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { useFormik } from 'formik';
import { AssignUserFormWrapper, FormMessage, FormTitle } from '../../../../shared/ui/styles';
import {
  EVENTS_RECIPIENT_ACTION_TYPE,
  ERROR_ASSIGN_RECIPIENT_AMPLITUDE_EVENTS_MAP,
  ASSIGN_RECIPIENT_INITIAL_VALUES,
  ASSIGN_RECIPIENT_PROXY_OPTIONS,
  AssignRecipientFormType,
} from '../../const/const';
import { assignRecipient, createRecipient, recipientAssignmentStatus } from '../../api/recipientApi';
import { Box } from '@/components/Box/Box';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Button } from '@/components/Button/Button';
import { loadUserInfo } from '@/bundle/shared/api';
import { useAssignUser } from '@/bundle/shared/hooks/useAssignUser';
import { ASSIGN_USER_SCHEMA } from '@/bundle/_OrgAdmin/shared/const/const';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';
import { FormikSelectableTabs } from '@/components/form/fields/FormikSelectableTabs/FormikSelectableTabs';
import { FormikSwitchToggle } from '@/components/form/fields/FormikSwitchToggle/FormikSwitchToggle';
import { Notification } from '@/components/Notification/Notification';
import { getExcludedResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { getIsAvailableToAssign } from '@/helpers/userHelpers';
import { SearchUserEmailInput } from '@/bundle/shared/components/SearchUserEmailInput/SearchUserEmailInput';
import { USER_ROLE_MAP } from '@/const/user';
import { TRIAL_MODE_SUBSCRIPTION_REQUIRED_ERROR_MESSAGE, HTTP_BAD_REQUEST_STATUS } from '@/api/const';
import { useSubscriptionRequiredError } from '@/bundle/shared/hooks/useSubscriptionRequiredError';
import { SubscriptionRequiredModal } from '@/bundle/shared/components/modals/SubscriptionRequiredModal';
import { getAcceptMasterServiceAgreementPath } from '@/bundle/Auth/AdminOnboardingFlow/AcceptMasterServiceAgreementPage/path';
import { ONBOARDING_GUIDE_STEP_ID } from '@/context/OnboardingGuideContext/const';
import { useOnboardingGuide } from '@/context/OnboardingGuideContext/onboardingGuideContext';

type AssignRecipientFormPropsType = {
  allowedIdVerification?: boolean;
  onCreateRecipient: () => void;
  onAssignRecipient: () => void;
};

const getAmplitudeResponseType = (isProxy: boolean) => {
  return isProxy ? 'proxy' : 'recipient';
};

export const AssignRecipientForm = ({
  allowedIdVerification,
  onCreateRecipient,
  onAssignRecipient,
}: AssignRecipientFormPropsType) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [submittedEmailRequest, setSubmittedEmailRequest] = useState(false);
  const [assignedUserId, setAssignedUserId] = useState<null | string>(null);
  const { hasSubscriptionRequiredError, resetSubscriptionRequiredError, handleSubscriptionRequiredError } =
    useSubscriptionRequiredError();
  const { showStep } = useOnboardingGuide();

  const { data: assignmentStatusData } = useQuery({
    queryKey: ['load_assignment_status', assignedUserId],
    queryFn: () => recipientAssignmentStatus(assignedUserId),
    enabled: !!assignedUserId,
  });

  const { data: loadUserData } = useQuery({
    queryKey: ['load_user', assignedUserId],
    queryFn: () => loadUserInfo(assignedUserId),
    enabled: !!assignedUserId && !!assignmentStatusData?.body?.is_available_to_assign,
  });

  const {
    mutate: createRecipientMutate,
    isPending: isPendingCreateRecipient,
    data: createRecipientData,
    reset: resetCreateRecipient,
  } = useMutation({
    mutationKey: ['create_recipient'],
    mutationFn: (values: AssignRecipientFormType) => {
      const payload = {
        ...values,
        is_pd_provider_kyc_required: !!values.is_pd_provider_kyc_required,
      };

      return createRecipient(id, payload);
    },
    onSuccess(response, variables) {
      if (response?.error) {
        return handleSubscriptionRequiredError(response?.error);
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.AssignNewRecipient, {
        responseType: getAmplitudeResponseType(variables.is_proxy),
      });
      onCreateRecipient();
    },
  });

  const {
    mutate: assignRecipientMutate,
    isPending: isPendingAssignRecipient,
    data: assignRecipientData,
    reset: resetAssignRecipient,
  } = useMutation({
    mutationKey: ['assign_recipient'],
    mutationFn: (values: AssignRecipientFormType) => {
      const userData = loadUserData?.body;

      const payload = {
        user_id: userData?.id,
        is_proxy: values.is_proxy,
        is_pd_provider_kyc_required: !!values.is_pd_provider_kyc_required,
      };

      return assignRecipient(id, payload);
    },
    onSuccess(response, variables) {
      if (response?.error) {
        return handleSubscriptionRequiredError(response?.error);
      }

      const eventType = EVENTS_RECIPIENT_ACTION_TYPE[assignmentStatusData?.body?.code];

      if (eventType) {
        amplitudeService.logEvent(eventType, { responseType: getAmplitudeResponseType(variables.is_proxy) });
      }

      onAssignRecipient();
    },
  });

  useEffect(() => {
    showStep(ONBOARDING_GUIDE_STEP_ID.ASSIGN_RECIPIENT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const assignmentStatusBody = assignmentStatusData?.body;

    if (assignmentStatusBody && !assignmentStatusBody?.is_available_to_assign) {
      const eventType = ERROR_ASSIGN_RECIPIENT_AMPLITUDE_EVENTS_MAP[assignmentStatusBody?.code];

      if (eventType) {
        amplitudeService.logEvent(ERROR_ASSIGN_RECIPIENT_AMPLITUDE_EVENTS_MAP[assignmentStatusBody?.code]);
      }
    }
  }, [assignmentStatusData?.body]);

  const user = loadUserData?.body;
  const isAvailableToAssign = getIsAvailableToAssign(assignmentStatusData?.body?.is_available_to_assign);
  // Disable user fields for all cases, except when new user will be assigned
  const isDisabledValidation = !!user || (assignedUserId && !isAvailableToAssign);

  const formik = useFormik<AssignRecipientFormType>({
    initialValues: ASSIGN_RECIPIENT_INITIAL_VALUES,
    validationSchema: isDisabledValidation ? null : ASSIGN_USER_SCHEMA,
    onSubmit: (values) => {
      if (assignedUserId) {
        assignRecipientMutate(values);

        return;
      }

      createRecipientMutate(values);
    },
  });

  const searchUserEmail = (userId: string) => {
    formik.setErrors({});
    setAssignedUserId(userId);
    setSubmittedEmailRequest(true);
  };

  const submitUserEmail = () => {
    if (!formik.touched.email && formik.errors?.email) {
      formik.setTouched({ email: true });
    }
  };

  const onResetData = () => {
    setAssignedUserId(null);
    setSubmittedEmailRequest(false);
    resetCreateRecipient();
    resetAssignRecipient();
    formik.resetForm();
    formik.setFieldValue('email', formik.values.email);

    if (allowedIdVerification) {
      formik.setFieldValue('is_pd_provider_kyc_required', true);
    }
  };

  const createRecipientError = createRecipientData?.error;
  const assignRecipientError = assignRecipientData?.error;
  const apiError = createRecipientError || assignRecipientError;
  const formError = getExcludedResponseError(apiError, TRIAL_MODE_SUBSCRIPTION_REQUIRED_ERROR_MESSAGE);
  const hasApiError = apiError?.statusCode === HTTP_BAD_REQUEST_STATUS;

  useAssignUser(
    loadUserData?.body,
    formik,
    onResetData,
    assignedUserId,
    submittedEmailRequest,
    assignmentStatusData?.body?.is_available_to_assign,
    apiError
  );

  const assignmentStatus = assignmentStatusData?.body?.code;
  const isLoading = isPendingCreateRecipient || isPendingAssignRecipient;
  const userIsNotAvailableToAssign = (assignedUserId && !isAvailableToAssign) || hasApiError;
  const isDisabledForm = !!assignedUserId || !submittedEmailRequest || hasApiError;
  const isDisabledAssign = !isAvailableToAssign || !submittedEmailRequest || hasApiError;

  // Note: set "id verification" switch toggle when <allowedIdVerification> updating from user context
  useEffect(() => {
    if (allowedIdVerification) {
      formik.setFieldValue('is_pd_provider_kyc_required', true);
    } else {
      formik.setFieldValue('is_pd_provider_kyc_required', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedIdVerification]);

  return (
    <AssignUserFormWrapper>
      <FormTitle>Assign Recipient</FormTitle>
      <FormMessage>Please indicate who will be receiving this wire sending.</FormMessage>
      {formError && (
        <Notification variant='error' marginBottom='24px' marginTop='-10px'>
          {formError}
        </Notification>
      )}
      <FormikForm value={formik}>
        <SearchUserEmailInput
          role={USER_ROLE_MAP.RECIPIENT}
          email={formik.values.email}
          onSearch={searchUserEmail}
          onSubmit={submitUserEmail}
          assignmentStatus={assignmentStatus}
          error={formik.errors?.email}
          apiError={apiError}
        />
        <FormikPhoneInput
          name='mobile_phone'
          label='Mobile Phone Number*'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='first_name'
          label='First Name*'
          placeholder='Enter First Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='middle_name'
          label='Middle Name'
          placeholder='Enter Middle Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />
        <FormikInput
          name='last_name'
          label='Last Name*'
          placeholder='Enter Last Name'
          disabled={isDisabledForm}
          apiError={apiError}
        />

        <Box marginBottom='22px'>
          <FormikSelectableTabs
            label='The wire info will be provided by:'
            name='is_proxy'
            options={ASSIGN_RECIPIENT_PROXY_OPTIONS}
            disabled={userIsNotAvailableToAssign}
          />
        </Box>

        {allowedIdVerification && (
          <FormikSwitchToggle
            name='is_pd_provider_kyc_required'
            label='ID verification required'
            size='small'
            disabled={userIsNotAvailableToAssign}
          />
        )}

        <Box alignItems='center' justifyContent='flex-end' marginTop='14px'>
          <Button
            width={172}
            size='medium'
            type='submit'
            disabled={isDisabledAssign}
            mobileStretch
            isLoading={isLoading}
            id={ONBOARDING_GUIDE_STEP_ID.ASSIGN_RECIPIENT}
          >
            Assign
          </Button>
        </Box>
      </FormikForm>
      <SubscriptionRequiredModal
        isOpen={hasSubscriptionRequiredError}
        onClose={resetSubscriptionRequiredError}
        onConfirm={() => navigate(getAcceptMasterServiceAgreementPath())}
      >
        <li>Send your first wire request now</li>
        <li>Add additional team members and administrators</li>
        <li>Access advanced ID verification tools</li>
      </SubscriptionRequiredModal>
    </AssignUserFormWrapper>
  );
};
